import { useEffect, useRef, useState } from "react";

type Quote = {
  text: string;
  author: string;
  role: string;
  portrait: string;
};

const QUOTES: Quote[] = [
  {
    text:
      "The exercise is to shuffle the cards and see established companies in a new light.",
    author: "Petter Stordalen",
    role: "Founder of Strawberry Group",
    portrait:
      "https://readdy.ai/api/search-image?query=Confident%20mature%20businessman%20portrait%20in%20warm%20natural%20light%20wearing%20smart%20casual%20knit%20sweater%20standing%20near%20cream%20colored%20interior%20wall%20editorial%20magazine%20lifestyle%20photography%20soft%20diffused%20studio%20lighting%20warm%20beige%20tones%20candid%20expression%20cinematic%20high%20detail&width=680&height=880&seq=founder-portrait-01&orientation=portrait",
  },
  {
    text:
      "Sell the berries you have — they are the only ones you can sell. Make the most out of what you have.",
    author: "Petter Stordalen",
    role: "Founder of Strawberry Group",
    portrait:
      "https://readdy.ai/api/search-image?query=Editorial%20portrait%20of%20a%20thoughtful%20mature%20entrepreneur%20looking%20away%20from%20camera%20in%20soft%20natural%20window%20light%20warm%20neutral%20studio%20background%20hands%20gently%20crossed%20contemplative%20expression%20cinematic%20magazine%20photography%20muted%20warm%20tones%20high%20detail&width=680&height=880&seq=quote-portrait-02&orientation=portrait",
  },
  {
    text:
      "Dare to be different. A culture of passion and courage is what creates real value.",
    author: "Petter Stordalen",
    role: "Founder of Strawberry Group",
    portrait:
      "https://readdy.ai/api/search-image?query=Cinematic%20editorial%20portrait%20of%20a%20smiling%20mature%20businessman%20in%20casual%20open%20collar%20shirt%20standing%20beside%20a%20large%20window%20warm%20afternoon%20light%20neutral%20cream%20studio%20wall%20behind%20him%20relaxed%20confident%20pose%20fine%20art%20magazine%20photography%20high%20detail&width=680&height=880&seq=quote-portrait-03&orientation=portrait",
  },
];

export default function QuoteSection() {
  const [index, setIndex] = useState(0);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
  const [hovering, setHovering] = useState(false);
  const portraitRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Auto-advance
  useEffect(() => {
    if (hovering) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % QUOTES.length);
    }, 6500);
    return () => window.clearInterval(id);
  }, [hovering]);

  const goPrev = () =>
    setIndex((i) => (i - 1 + QUOTES.length) % QUOTES.length);
  const goNext = () => setIndex((i) => (i + 1) % QUOTES.length);

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = portraitRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -py * 10, ry: px * 12 });
  };

  const current = QUOTES[index];
  const label = `0${index + 1}`;

  return (
    <section
      ref={sectionRef}
      className="relative w-full"
      style={{
        background: "#000",
        padding: "80px 40px 120px",
        fontFamily: "'Space Grotesk', system-ui, sans-serif",
      }}
    >
      <div className="mx-auto" style={{ maxWidth: 1440 }}>
        <div
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => {
            setHovering(false);
            setTilt({ rx: 0, ry: 0 });
          }}
          className="grid gap-6 relative overflow-hidden"
          style={{
            gridTemplateColumns: "minmax(260px, 340px) 1fr",
            background: "#DBEAFE",
            borderRadius: 24,
            padding: 40,
            color: "#0B1B4D",
            alignItems: "stretch",
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(40px)",
            transition:
              "opacity 0.9s cubic-bezier(0.2, 0.8, 0.2, 1), transform 0.9s cubic-bezier(0.2, 0.8, 0.2, 1)",
          }}
        >
          {/* Giant background quote mark */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: -60,
              right: -20,
              fontSize: 460,
              lineHeight: 1,
              fontWeight: 700,
              color: "#0B1B4D",
              opacity: 0.06,
              pointerEvents: "none",
              userSelect: "none",
              fontFamily: "Georgia, serif",
              transition: "transform 0.6s ease",
              transform: hovering ? "scale(1.06) rotate(-3deg)" : "scale(1)",
            }}
          >
            &ldquo;
          </div>

          {/* Portrait with 3D tilt & crossfade */}
          <div
            ref={portraitRef}
            onMouseMove={handleMove}
            className="relative overflow-hidden"
            style={{
              borderRadius: 16,
              background: "#93C5FD",
              minHeight: 380,
              perspective: 900,
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) scale(${
                  hovering ? 1.05 : 1
                })`,
                transformStyle: "preserve-3d",
                transition:
                  "transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)",
              }}
            >
              {QUOTES.map((q, i) => (
                <img
                  key={q.portrait}
                  src={q.portrait}
                  alt={q.author}
                  className="w-full h-full"
                  style={{
                    position: "absolute",
                    inset: 0,
                    objectFit: "cover",
                    objectPosition: "top",
                    opacity: i === index ? 1 : 0,
                    transform:
                      i === index ? "scale(1)" : "scale(1.08)",
                    transition:
                      "opacity 0.8s ease, transform 1.2s cubic-bezier(0.2, 0.8, 0.2, 1)",
                  }}
                />
              ))}
              {/* subtle shine */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%)",
                  transform: `translateX(${hovering ? 30 : -60}%)`,
                  transition: "transform 1.2s ease",
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>

          {/* Quote content */}
          <div
            className="flex flex-col justify-between relative"
            style={{ minHeight: 380 }}
          >
            <div>
              <div
                key={label}
                style={{
                  fontSize: 44,
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  lineHeight: 1,
                  animation: "qCounter 0.6s ease",
                }}
              >
                {label}
                <span
                  style={{
                    fontSize: 20,
                    fontWeight: 500,
                    opacity: 0.45,
                    marginLeft: 6,
                  }}
                >
                  / 0{QUOTES.length}
                </span>
              </div>

              <div
                style={{
                  marginTop: 40,
                  position: "relative",
                  minHeight: 200,
                }}
              >
                {QUOTES.map((q, i) => (
                  <p
                    key={i}
                    style={{
                      position: "absolute",
                      inset: 0,
                      fontSize: "clamp(24px, 2.4vw, 34px)",
                      lineHeight: 1.15,
                      letterSpacing: "-0.02em",
                      fontWeight: 500,
                      maxWidth: 720,
                      margin: 0,
                      opacity: i === index ? 1 : 0,
                      transform:
                        i === index
                          ? "translateY(0)"
                          : "translateY(14px)",
                      transition:
                        "opacity 0.55s ease, transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1)",
                      pointerEvents: i === index ? "auto" : "none",
                    }}
                  >
                    &ldquo;{q.text}&rdquo;
                  </p>
                ))}
              </div>
            </div>

            <div className="flex items-end justify-between mt-12 relative">
              <div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    transition: "opacity 0.4s ease",
                  }}
                  key={current.author + index}
                >
                  {current.author}
                </div>
                <div
                  style={{ fontSize: 14, color: "#4A5B8C" }}
                  key={current.role + index}
                >
                  {current.role}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Progress dots */}
                <div className="flex items-center gap-1 mr-2">
                  {QUOTES.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setIndex(i)}
                      aria-label={`Go to quote ${i + 1}`}
                      className="cursor-pointer"
                      style={{
                        width: i === index ? 24 : 8,
                        height: 8,
                        borderRadius: 9999,
                        background:
                          i === index ? "#0B1B4D" : "rgba(11,27,77,0.25)",
                        border: "none",
                        transition:
                          "width 0.4s cubic-bezier(0.2, 0.8, 0.2, 1), background 0.3s",
                      }}
                    />
                  ))}
                </div>
                <ArrowBtn direction="prev" onClick={goPrev} />
                <ArrowBtn direction="next" onClick={goNext} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes qCounter {
          from { opacity: 0.2; transform: translateY(-6px); }
          to   { opacity: 1;   transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}

function ArrowBtn({
  direction,
  onClick,
}: {
  direction: "prev" | "next";
  onClick?: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="whitespace-nowrap cursor-pointer flex items-center justify-center rounded-full relative overflow-hidden"
      style={{
        width: 44,
        height: 44,
        background: "#0B1B4D",
        color: "#DBEAFE",
        border: "none",
        transition: "transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)",
        transform: hover ? "scale(1.08)" : "scale(1)",
      }}
      aria-label={direction === "prev" ? "Previous" : "Next"}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "9999px",
          background: "#3B82F6",
          transform: hover ? "scale(1)" : "scale(0)",
          transition: "transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      />
      <i
        className={
          direction === "prev" ? "ri-arrow-left-line" : "ri-arrow-right-line"
        }
        style={{
          fontSize: 18,
          position: "relative",
          transform: hover
            ? direction === "next"
              ? "translateX(3px)"
              : "translateX(-3px)"
            : "translateX(0)",
          transition: "transform 0.3s ease",
        }}
      />
    </button>
  );
}